import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate as useRouter, useLocation } from 'react-router-dom';
import { fetchPublicSettings } from '../app/actions';
import { resolveAssetUrl } from '../lib/rpc';

interface User {
  id: string;
  email: string;
  role: string;
  companies: { companyId: string, companyName: string, role: string }[];
}

interface AuthContextType {
  user: User | null;
  theme: any;
  login: (userData: User) => void;
  updateUser: (updates: Partial<User>) => void;
  logout: () => void;
  loading: boolean;
  reloadTheme: () => Promise<void>;
  updateTheme: (newTheme: any) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  theme: {},
  login: () => {},
  updateUser: () => {},
  logout: () => {},
  loading: true,
  reloadTheme: async () => {},
  updateTheme: () => {}
});

/**
 * Dynamically swaps or resets favicon and apple-touch-icon links in the document head.
 * Sets the exact MIME type and cache-buster query parameter to bypass aggressive browser caching.
 */
export function applyFavicon(faviconUrl?: string) {
  if (typeof document === 'undefined') return;

  const resolved = faviconUrl ? resolveAssetUrl(faviconUrl) : '';

  // Remove existing dynamic and static icon tags to ensure clean swap
  const existingIcons = document.querySelectorAll("link[rel*='icon']");
  existingIcons.forEach(el => el.remove());

  if (resolved) {
    const sep = resolved.includes('?') ? '&' : '?';
    const cacheBusted = `${resolved}${sep}v=${Date.now()}`;
    const ext = resolved.split('.').pop()?.split('?')[0]?.toLowerCase();

    let mimeType = 'image/png';
    if (ext === 'svg') mimeType = 'image/svg+xml';
    else if (ext === 'ico') mimeType = 'image/x-icon';
    else if (ext === 'webp') mimeType = 'image/webp';

    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = mimeType;
    link.href = cacheBusted;
    document.head.appendChild(link);

    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    appleLink.href = cacheBusted;
    document.head.appendChild(appleLink);
  } else {
    // Re-apply official default icons
    const svgLink = document.createElement('link');
    svgLink.rel = 'icon';
    svgLink.type = 'image/svg+xml';
    svgLink.href = '/favicon.svg';
    document.head.appendChild(svgLink);

    const icoLink = document.createElement('link');
    icoLink.rel = 'icon';
    icoLink.type = 'image/x-icon';
    icoLink.href = '/favicon.ico';
    document.head.appendChild(icoLink);

    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    appleLink.href = '/icon-192.png';
    document.head.appendChild(appleLink);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [theme, setTheme] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const pathname = useLocation().pathname;

  const reloadTheme = useCallback(async () => {
    try {
      const settings = await fetchPublicSettings();
      if (settings && !settings.error) {
        setTheme(settings);
        applyFavicon(settings.BRANDING_FAVICON);
        if (settings.BRANDING_COLOR) {
          document.documentElement.style.setProperty('--primary', settings.BRANDING_COLOR);
          const metaTheme = document.querySelector('meta[name="theme-color"]');
          if (metaTheme) metaTheme.setAttribute('content', settings.BRANDING_COLOR);
        }
      }
    } catch (err) {
      console.error('Failed to load branding theme:', err);
    }
  }, []);

  const updateTheme = useCallback((newSettings: any) => {
    setTheme((prev: any) => {
      const merged = { ...prev, ...newSettings };
      return merged;
    });

    if ('BRANDING_FAVICON' in newSettings) {
      applyFavicon(newSettings.BRANDING_FAVICON);
    }
    if (newSettings.BRANDING_COLOR) {
      document.documentElement.style.setProperty('--primary', newSettings.BRANDING_COLOR);
      const metaTheme = document.querySelector('meta[name="theme-color"]');
      if (metaTheme) metaTheme.setAttribute('content', newSettings.BRANDING_COLOR);
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('dark_session');
    if (stored) {
       try {
          setUser(JSON.parse(stored));
       } catch(e) {}
    }
    setLoading(false);
    reloadTheme();
  }, [reloadTheme]);

  useEffect(() => {
    if (!loading) {
       const isCrmSubdomain = typeof window !== 'undefined' && window.location.hostname === 'crm.dark.tel';
       const isPublicRoute = isCrmSubdomain || pathname === '/login' || pathname === '/services/crm' || pathname.startsWith('/services/crm/');
       if (!user && !isPublicRoute) {
          router('/login');
       } else if (user && pathname === '/login') {
          router('/');
       }
    }
  }, [user, loading, pathname, router]);

  const login = (userData: User) => {
     setUser(userData);
     localStorage.setItem('dark_session', JSON.stringify(userData));
     router('/');
  };

  const updateUser = (updates: Partial<User>) => {
     setUser(prev => {
        if (!prev) return prev;
        const next = { ...prev, ...updates };
        localStorage.setItem('dark_session', JSON.stringify(next));
        return next;
     });
  };

  const logout = () => {
     setUser(null);
     localStorage.removeItem('dark_session');
     router('/login');
  };

  return (
    <AuthContext.Provider value={{ user, theme, login, updateUser, logout, loading, reloadTheme, updateTheme }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
